<template>
  <div class="question-sections-container">
    <!-- Main Assessment Questions -->
    <div class="assessment-section">
      <div class="assessment-header">
        <h2>Assessment Questions</h2>
      </div>
      <QuestionList
        :questions="questions"
        @select-response="selectResponse"
        @show-image="showImage"
        @show-information="showInformation"
        @show-measurement-tool="showMeasurementTool"
        @show-tooth-selector="showToothSelector"
      />
    </div>

    <!-- Inline Screening Questions (appears when needed) -->
    <div v-if="pendingScreeningSystemType !== null" class="inline-screening-section">
      <InlineScreeningQuestions 
        :system-type="pendingScreeningSystemType"
        :title="getSystemTypeName(pendingScreeningSystemType)"
        :preselected-screening-ids="preselectedScreeningIds"
        @screening-confirmed="handleScreeningConfirmed"
        @screening-cancelled="handleScreeningCancelled"
        @error="handleScreeningError"
      />
    </div>

    <!-- Additional Cross-System Questions -->
    <div v-if="crossSystemQuestions.length > 0" class="additional-questions-section">
      <div class="assessment-header">
        <div class="header-with-reset">
          <h2>{{ additionalQuestionsTitle }}</h2>
          <button 
            class="reset-button"
            @click="handleResetCrossQuestions"
            title="Reset and reselect screening questions"
          >
            <svg class="reset-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd" />
            </svg>
            Reset Filters
          </button>
        </div>
      </div>
      <QuestionList
        :questions="crossSystemQuestions"
        :starting-index="questions.length"
        @select-response="selectResponse"
        @show-image="showImage"
        @show-information="showInformation"
        @show-measurement-tool="showMeasurementTool"
        @show-tooth-selector="showToothSelector"
      />
    </div>
  </div>
</template>

<script>
import QuestionList from './QuestionList.vue'
import InlineScreeningQuestions from './InlineScreeningQuestions.vue'

export default {
  name: 'SurveyQuestionSections',
  
  components: {
    QuestionList,
    InlineScreeningQuestions
  },

  props: {
    questions: {
      type: Array,
      required: true
    },
    crossSystemQuestions: {
      type: Array,
      default: () => []
    },
    pendingScreeningSystemType: {
      type: Number,
      default: null
    },
    isRCatSystem: {
      type: Boolean,
      default: false
    },
    preselectedScreeningIds: {
      type: Array,
      default: () => []
    }
  },

  computed: {
    additionalQuestionsTitle() {
      if (this.crossSystemQuestions.length === 0) {
        return ''; 
      }
      
      const systemType = localStorage.getItem('@app-system');

      if (systemType == "ecat") {
        return 'Endodontic Assessment';
      } else if (systemType === "rcat") {
        return 'Endodontic Assessment';
      } else {
        return 'Additional Questions';
      }
    }
  },

  methods: {
    getSystemTypeName(systemType) {
      switch(systemType) {
        case 0: return 'E-CAT';
        case 1: return 'R-CAT';
        default: return 'System';
      }
    },

    // Question interaction methods
    selectResponse(data) {
      this.$emit('select-response', data);
    },

    showImage(imageBase64) {
      this.$emit('show-image', imageBase64);
    },

    showInformation(item) {
      this.$emit('show-information', item);
    },

    showMeasurementTool(question) {
      this.$emit('show-measurement-tool', question);
    },

    showToothSelector(question) {
      this.$emit('show-tooth-selector', question);
    },

    // Inline screening methods
    handleScreeningConfirmed(data) {
      this.$emit('screening-confirmed', data);
    },

    handleScreeningCancelled() {
      this.$emit('screening-cancelled');
    },

    handleScreeningError(errorMessage) {
      this.$emit('screening-error', errorMessage);
    },

    // Reset cross-system questions
    handleResetCrossQuestions() {
      this.$emit('reset-cross-questions');
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../breakpoints.scss";

.question-sections-container {
  margin-bottom: 24px;
}

.assessment-section {
  margin-bottom: 32px;
}

.assessment-header {
  margin-bottom: 24px;
  margin-top: 20px;

  h2 {
    font-size: 22px;
    font-weight: 600;
    color: #111827;
    margin: 0;

    @include md {
      font-size: 24px;
    }
  }
}

.header-with-reset {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.reset-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f8f9fa;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #4b5563;
  }

  &:active {
    transform: translateY(1px);
  }

  .reset-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

.additional-questions-section {
  margin-top: 40px;
}

.inline-screening-section {
  margin: 32px 0;
  
  // Add some visual separation
  &::before {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, transparent, #E5E7EB 20%, #E5E7EB 80%, transparent);
    margin-bottom: 32px;
  }
  
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, transparent, #E5E7EB 20%, #E5E7EB 80%, transparent);
    margin-top: 32px;
  }
}

// Add animation for when inline screening appears
.inline-screening-section {
  animation: slideInFromTop 0.4s ease-out;
}

@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>